import React, {
  useCallback,
  useEffect,
  useMemo,
  type FC,
  type ReactNode,
} from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useOverlayContext } from '@providers/OverlayProvider';
import { useAssistant } from '@core/assistant/providers/Provider';
import { isMapFeatureFlagEnabled } from '@helpers/events';
import OVERLAYS from '@constants/overlays';

import { EditorNodeKind, getSpotType, SpotType } from '../editorCategories';
import { useCanUseMapEditor } from '../useCanUseMapEditor';
import { useMapEditorOrNull } from '../MapEditorContext';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import { findCatalogElement } from '../mapEditorCatalog';
import { START_ROOM_KEY } from '../mapEditorUnreal';
import { useBrandingEntityCatalogue } from '../useBrandingEntityCatalogue';
import { describeFrameSize } from '../frameSize';
import { useEditorMode } from '../EditorModeContext';
import { useEditorStyles } from '../styles';
import { useDrift } from '../DriftContext';
import { useEditorAiData } from '../AiScripts/EditorAiDataContext';
import {
  filterByScope,
  hasAssistantContentForTrigger,
} from '../AiScripts/aggregate';
import { useRegionIndex } from '../AiScripts/useRegionIndex';
import MapColorsInspector from './MapColorsInspector';
import BrandingEntityInspector from './BrandingEntityInspector';
import RealtimeEntityInspector from './RealtimeEntityInspector';
import SpotInspector from './SpotInspector';
import HotspotInspector from './HotspotInspector';
import AIScriptInspector from './AIScriptInspector';
import MapEditorInspector from './MapEditorInspector';
import SegmentInspector from './SegmentInspector';
import {
  blinkSpotInUnreal,
  teleportToSpotInUnreal,
} from '../brandingSpotsUnreal';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

const getSpotTypeIcon = (type: SpotType, sx: object) => {
  if (type === 'VIDEO') {
    return <PlayArrowOutlinedIcon sx={sx} />;
  }
  if (type === 'QR') {
    return <QrCodeOutlinedIcon sx={sx} />;
  }
  return <ImageOutlinedIcon sx={sx} />;
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const driftChipStyles = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    padding: '2px 7px',
    borderRadius: '6px',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  missing: {
    backgroundColor: 'rgba(245, 166, 35, 0.18)',
    color: '#f0b357',
  },
  fresh: {
    backgroundColor: 'rgba(124, 108, 240, 0.22)',
    color: '#c8bdff',
  },
};

const Inspector = () => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const { selectedNode, clearSelection, pinSpotHighlight } = useEditorMode();
  const { spotDrift } = useDrift();
  const {
    roomSpots,
    brandingSpots,
    brandingHotspots,
    realtimeEntities,
    currentRoom,
    currentEvent,
    currentBrandingId,
    isBrandingModeEnabled,
    selectedCustomizationTargetSize,
    setSelectedCustomizationUeId,
    setIsEditorInspectorOpen,
    requestAssistantRegionChange,
    suppressNextCustomizationSelect,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { isOverlayCurrentlyOpen } = useOverlayContext();
  // Read for the panel's title only, and optionally: this panel is the shared chrome of every focus,
  // so the map editor's provider must not become a requirement for rendering it. The segment panel
  // resolves the same two itself, which is not a duplication worth removing — the header is chrome
  // and the body is the feature, and threading the element down would couple the two.
  const mapEditorDraft = useMapEditorOrNull();
  const mapEditorRooms = mapEditorDraft?.rooms ?? [];
  const startElement = mapEditorDraft?.startElement ?? '';
  const { elements: catalogElements } = useMapEditorCatalog();
  const { scripts, ragItems } = useEditorAiData();
  const assistant = useAssistant();
  const mapName =
    currentRoom?.map?.name ?? assistant?.currentMapName ?? undefined;
  const regionIndex = useRegionIndex(mapName);

  // Notify the rest of the app (specifically the Assistant wrapper) when the
  // inspector is visible so Noa hides itself for the duration. Cleanup restores
  // the assistant when EditorMode unmounts.
  const isInspectorVisible = !!selectedNode;
  useEffect(() => {
    setIsEditorInspectorOpen(isInspectorVisible);
    return () => {
      setIsEditorInspectorOpen(false);
    };
  }, [isInspectorVisible, setIsEditorInspectorOpen]);

  const isBlockingOverlayOpen =
    isOverlayCurrentlyOpen(OVERLAYS.ADMIN_TOOLS) ||
    isOverlayCurrentlyOpen(OVERLAYS.PROFILE);
  useEffect(() => {
    if (isBlockingOverlayOpen && selectedNode) {
      clearSelection();
      setSelectedCustomizationUeId(null);
    }
  }, [
    isBlockingOverlayOpen,
    selectedNode,
    clearSelection,
    setSelectedCustomizationUeId,
  ]);

  const handleCloseInspector = () => {
    clearSelection();
    setSelectedCustomizationUeId(null);
  };

  const spotUeId =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? selectedNode.ueIdentifier
      : null;
  const spotDriftRow = spotUeId
    ? spotDrift.rows.find(
        (row) =>
          normalizeUeIdentifier(row.ueIdentifier) ===
          normalizeUeIdentifier(spotUeId),
      ) ?? null
    : null;
  // No longer shown as a chip — the configuration's author cannot fix a build that dropped the
  // anchor. Kept only to disable the two actions that would silently do nothing on it.
  const isOrphanedSpot = spotDriftRow?.status === 'orphaned';
  const isMissingSpot = spotDriftRow?.status === 'missing';
  const map = currentRoom?.map;
  const canTeleportToSpot = isMapFeatureFlagEnabled(
    currentEvent,
    map,
    'TELEPORT_UE_ACTION',
  );
  const canBlinkSpot = isMapFeatureFlagEnabled(
    currentEvent,
    map,
    'RUNTIME_SPOT_UPDATE_UE',
  );
  const canUseMapEditor = useCanUseMapEditor();
  const { entities: brandingEntities } = useBrandingEntityCatalogue();

  // useEditorAiData's scripts/ragItems are deliberately unscoped (every script
  // and knowledge item the admin has authored, across every map and branding)
  // so the SceneTree can list all of them. hasAssistantContentForTrigger below
  // needs the opposite: whether THIS map+branding has real content for the
  // destination region — the same map+branding scope aggregateEntities already
  // applies for the tree/inspector, and that AiInspectorHeader's
  // teleport-to-trigger gets for free by reading entity.scriptState/
  // contextState (both pre-scoped). Without this, a same-named region (e.g.
  // "Expo #2") configured on a different map or branding falsely counts as
  // configured here.
  const { scripts: scopedScripts, ragItems: scopedRagItems } = useMemo(
    () =>
      filterByScope({
        scripts,
        ragItems,
        map,
        brandingId: currentBrandingId ?? undefined,
      }),
    [scripts, ragItems, map, currentBrandingId],
  );

  const handleTeleportToSpot = useCallback(() => {
    if (!spotUeId) {
      return;
    }
    teleportToSpotInUnreal(sendMessageToUnreal, spotUeId);

    // Keep this spot highlighted in the SceneTree even if the branch below
    // clears selectedNode to close this panel for the assistant (MV-1881).
    pinSpotHighlight(spotUeId);

    // If the spot's owning region has real assistant content — a script with
    // actual steps, or matching knowledge, not just an empty draft record —
    // surface it the same way walking into that region in-world would: the
    // Inspector pane occupies the same screen area as Noa, so it must close
    // first. Mirrors AiInspectorHeader's teleport-to-trigger handling: UE's
    // region-enter overlap event doesn't reliably fire for a teleport, so we
    // notify the assistant of the destination directly instead of waiting
    // for it. Provider.tsx's pendingAssistantRegionTrigger replay effect
    // force-opens the assistant as part of handling this (bypassing the
    // branding-mode guard, since this is a deliberate admin action) — no
    // separate requestAssistantOpen() call needed here.
    const regionName = regionIndex.spotToRegion[spotUeId.toLowerCase()];
    if (
      regionName &&
      hasAssistantContentForTrigger(scopedScripts, scopedRagItems, regionName)
    ) {
      // Unreal echoes CUSTOMISATION_ITEM_SELECTED for this spot shortly
      // after the teleport lands — without this, UESelectionBridge would
      // re-select it and reopen the pane we're about to close.
      suppressNextCustomizationSelect(spotUeId);
      clearSelection();
      setSelectedCustomizationUeId(null);
      requestAssistantRegionChange(regionName, true);
    }
  }, [
    spotUeId,
    sendMessageToUnreal,
    pinSpotHighlight,
    regionIndex,
    scopedScripts,
    scopedRagItems,
    clearSelection,
    setSelectedCustomizationUeId,
    requestAssistantRegionChange,
    suppressNextCustomizationSelect,
  ]);

  const handleBlinkSpot = useCallback(() => {
    if (!spotUeId) {
      return;
    }
    blinkSpotInUnreal(sendMessageToUnreal, spotUeId);
  }, [spotUeId, sendMessageToUnreal]);

  let titleIcon: React.ReactNode = null;
  let titleText = '';
  let subtitleText: string | null = null;
  let body: React.ReactNode = null;
  // True when the body brings its own scrolling pane, so the wrapper below must
  // not add a second one.
  let bodyOwnsScroll = false;

  if (selectedNode?.kind === EditorNodeKind.MAP_AND_COLORS) {
    titleIcon = <PaletteOutlinedIcon sx={styles.panelTitleIcon} />;
    titleText = translate('editorMode.mapAndColors');
    body = <MapColorsInspector />;
  } else if (selectedNode?.kind === EditorNodeKind.SPOT) {
    const ueId = selectedNode.ueIdentifier;
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    const selectedUeId = normalizeUeIdentifier(ueId);
    const spot = Array.isArray(source)
      ? (source as Array<Record<string, unknown>>).find(
          (s) =>
            typeof (s as { ueIdentifier?: unknown }).ueIdentifier ===
              'string' &&
            normalizeUeIdentifier(
              (s as { ueIdentifier: string }).ueIdentifier,
            ) === selectedUeId,
        )
      : null;
    const type = getSpotType(spot ?? {});
    const name =
      typeof (spot as { name?: unknown } | null)?.name === 'string'
        ? ((spot as { name: string }).name as string)
        : ueId;
    titleIcon = getSpotTypeIcon(type, styles.panelTitleIcon);
    titleText = name;
    subtitleText = ueId;
    body = <SpotInspector key={ueId} ueIdentifier={ueId} />;
  } else if (selectedNode?.kind === EditorNodeKind.HOTSPOT) {
    const hotspotId = selectedNode.hotspotId;
    const source = Array.isArray(brandingHotspots) ? brandingHotspots : [];
    const hotspot = (source as Array<Record<string, unknown>>).find(
      (h) =>
        (h as { brandingHotspotId?: unknown }).brandingHotspotId === hotspotId,
    );
    const is3D =
      String((hotspot?.type as string) ?? '').toUpperCase() === 'OBJECT_3D';
    const hotspotName =
      typeof hotspot?.name === 'string' && hotspot.name.length > 0
        ? (hotspot.name as string)
        : hotspotId;
    titleIcon = is3D ? (
      <ExtensionOutlinedIcon sx={styles.panelTitleIcon} />
    ) : (
      <PlaceOutlinedIcon sx={styles.panelTitleIcon} />
    );
    titleText = hotspotName;
    subtitleText = hotspotId;
    body = <HotspotInspector key={hotspotId} hotspotId={hotspotId} />;
  } else if (
    canUseMapEditor &&
    selectedNode?.kind === EditorNodeKind.MAP_LAYOUT
  ) {
    titleIcon = <ViewQuiltOutlinedIcon sx={styles.panelTitleIcon} />;
    titleText = translate('editorMode.focusMapEditor', 'Map editor');
    body = <MapEditorInspector />;
  } else if (
    canUseMapEditor &&
    selectedNode?.kind === EditorNodeKind.MAP_SEGMENT
  ) {
    // Titled with the segment, and subtitled with its position in the walk rather than with its
    // placement key: the key is opaque and the position is what the author is looking at on the left.
    const { roomKey } = selectedNode;
    const isEntrance = roomKey === START_ROOM_KEY;
    const position = mapEditorRooms.findIndex((room) => room.key === roomKey);
    const segmentElement = isEntrance
      ? startElement && findCatalogElement(catalogElements, startElement)
      : position >= 0 &&
        findCatalogElement(catalogElements, mapEditorRooms[position].id);

    titleIcon = isEntrance ? (
      <HomeOutlinedIcon sx={styles.panelTitleIcon} />
    ) : (
      <ViewQuiltOutlinedIcon sx={styles.panelTitleIcon} />
    );
    titleText =
      (segmentElement && segmentElement.displayName) ||
      (isEntrance
        ? translate('editorMode.mapEditorEntrance', 'Entrance')
        : translate('editorMode.mapEditorSegment', 'Segment'));
    subtitleText = isEntrance
      ? translate(
          'editorMode.mapEditorEntranceSubtitle',
          'where visitors arrive',
        )
      : position >= 0
      ? translate(
          'editorMode.mapEditorSegmentPosition',
          'position {{position}}',
          {
            position: position + 1,
          },
        )
      : '';
    body = <SegmentInspector key={roomKey} roomKey={roomKey} />;
  } else if (selectedNode?.kind === EditorNodeKind.BRANDING_ENTITY) {
    const entityId = selectedNode.entityId;
    const entity = brandingEntities.find(
      (candidate) => candidate.entityId === entityId,
    );
    titleIcon = <CategoryOutlinedIcon sx={styles.panelTitleIcon} />;
    titleText = entity?.displayName || entityId;
    subtitleText = entityId;
    body = <BrandingEntityInspector key={entityId} entityId={entityId} />;
  } else if (selectedNode?.kind === EditorNodeKind.REALTIME_ENTITY) {
    const entityId = selectedNode.entityId;
    const entity = realtimeEntities.find(
      (candidate) => candidate.entityId === entityId,
    );
    titleIcon = <ViewInArOutlinedIcon sx={styles.panelTitleIcon} />;
    titleText = entity?.name || entity?.displayName || entityId;
    subtitleText = entityId;
    body = <RealtimeEntityInspector key={entityId} entityId={entityId} />;
    // Scrolls its own field list so Save and Delete can sit still below it.
    bodyOwnsScroll = true;
  }

  // Must run before any early return — React requires the hooks order to be
  // stable across renders. REGION_SCRIPT switches to a different render branch
  // below, so this useCallback has to live above that branch as well.
  const handleCopySubtitle = useCallback(async () => {
    if (!subtitleText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(subtitleText);
    } catch (copyError) {
      console.warn(
        `Failed to copy identifier: ${(copyError as Error)?.message}`,
        { ueIdentifier: subtitleText },
      );
    }
  }, [subtitleText]);

  // What is known about the selected surface's size: its shape, then how big it is on the wall.
  //
  // It printed the reported pair verbatim until 2026-09-04 — `1200 × 675` beside an upload control,
  // which reads as a pixel target and is not one, since UE reports world extents. See frameSize.ts.
  const spotSizeLabel =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? describeFrameSize(
          selectedCustomizationTargetSize?.width,
          selectedCustomizationTargetSize?.height,
          translate,
        )
      : null;

  // REGION_SCRIPT renders its own glass panel + header (direction B design),
  // so it bypasses the shared chrome below.
  if (selectedNode?.kind === EditorNodeKind.REGION_SCRIPT) {
    return (
      <AnimatePresence>
        <Box key="inspector-ai" sx={styles.inspectorWrap}>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ width: '100%' }}
          >
            <AIScriptInspector
              key={`script-${selectedNode.scriptId ?? selectedNode.trigger}`}
              trigger={selectedNode.trigger}
              scriptId={selectedNode.scriptId}
            />
          </motion.div>
        </Box>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {selectedNode && (
        <Box key="inspector" sx={styles.inspectorWrap}>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ width: '100%' }}
          >
            <Box
              sx={[
                styles.glassPanel,
                {
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100vh - 208px)',
                  overflow: 'hidden',
                },
              ]}
            >
              <Box sx={styles.panelHeader}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box sx={[styles.panelTitleRow, { minWidth: 0 }]}>
                    {titleIcon}
                    <Tooltip
                      title={titleText}
                      placement="bottom"
                      enterDelay={400}
                    >
                      <Typography
                        sx={[
                          styles.panelTitle,
                          {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          },
                        ]}
                      >
                        {titleText}
                      </Typography>
                    </Tooltip>
                    {spotUeId && spotDriftRow && isMissingSpot ? (
                      <Box
                        component="span"
                        sx={[
                          driftChipStyles.base,
                          spotDriftRow.isNew
                            ? driftChipStyles.fresh
                            : driftChipStyles.missing,
                        ]}
                      >
                        {spotDriftRow.isNew
                          ? translate('editorMode.driftChipNew')
                          : translate('editorMode.driftChipMissing')}
                      </Box>
                    ) : null}
                  </Box>
                  {subtitleText && (
                    <Box sx={styles.panelSubtitleRow}>
                      <Tooltip
                        title={subtitleText}
                        placement="bottom"
                        enterDelay={400}
                      >
                        <Typography sx={styles.panelSubtitle}>
                          {subtitleText}
                        </Typography>
                      </Tooltip>
                      <IconButton
                        onClick={handleCopySubtitle}
                        sx={styles.panelSubtitleCopy}
                        size="small"
                        aria-label={translate(
                          'editorMode.copyIdentifier',
                          'Copy identifier',
                        )}
                        data-testid="inspector-copy-id-btn"
                      >
                        <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                      {spotSizeLabel && (
                        <Tooltip
                          title={translate(
                            'editorMode.surfaceAspect',
                            'Shape and size of this surface — match the ratio so your image is not cropped',
                          )}
                          placement="bottom"
                          enterDelay={400}
                        >
                          <Typography
                            component="span"
                            sx={styles.panelSubtitleSize}
                            data-testid="inspector-surface-size"
                          >
                            {spotSizeLabel}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
                {spotUeId && canTeleportToSpot && (
                  <Tooltip
                    title={translate('editorMode.teleportToSpot')}
                    placement="bottom"
                    enterDelay={400}
                  >
                    <span>
                      <IconButton
                        onClick={handleTeleportToSpot}
                        sx={styles.panelIconButton}
                        size="small"
                        disabled={isOrphanedSpot}
                        aria-label={translate(
                          'editorMode.teleportToSpot',
                          'Teleport to spot',
                        )}
                        data-testid="inspector-teleport-btn"
                      >
                        <MyLocationOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {spotUeId && canBlinkSpot && (
                  <Tooltip
                    title={translate('editorMode.highlightSpot')}
                    placement="bottom"
                    enterDelay={400}
                  >
                    <span>
                      <IconButton
                        onClick={handleBlinkSpot}
                        sx={styles.panelIconButton}
                        size="small"
                        disabled={isOrphanedSpot}
                        aria-label={translate(
                          'editorMode.highlightSpot',
                          'Highlight spot',
                        )}
                        data-testid="inspector-blink-btn"
                      >
                        <CenterFocusStrongOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <IconButton
                  onClick={handleCloseInspector}
                  sx={styles.panelIconButton}
                  size="small"
                  aria-label={translate('editorMode.close', 'Close')}
                  data-testid="inspector-close-btn"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={bodyOwnsScroll ? styles.panelBodyStatic : styles.panelBody}
              >
                {body}
              </Box>
            </Box>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default Inspector;
